import type { PvpTeamLobby } from '@kenocyber/shared';
import type { PvpIntent } from './connection';
import type { PvpUiError } from './machine';
import type { PvpTeamClientState } from './team-connection';
import {
  PVP_TEAM_INITIAL_STATE,
  type PvpTeamMatchIdentity,
  type PvpTeamUiEvent,
  type PvpTeamUiState,
  pvpTeamReducer,
  teamMatchOf,
} from './team-machine';

/**
 * Everything the `team_3v3` interface DOES, with no React and no I/O in it.
 *
 * ## Why this is a second controller
 *
 * It holds the same kinds of resource as the duel's - one operation, one socket,
 * one generation, one pause lease, one arena, one recovery - and it acquires them
 * in a different order, from different routes, with one extra step nobody else
 * has: a lobby that must be FROZEN by its owner before any capability can exist.
 * The duel's controller has no place to put that, and giving it one would give it
 * a branch in `createMatch`, a branch in `resume`, a branch in the connection
 * subscription and a poll it must never run. Two objects, each of which is
 * entirely about one flow, is the smaller thing.
 *
 * ## Resources are OWNED, not inferred
 *
 * The fields under `owned` are the whole answer to "what does this thing hold
 * right now". Inferring any of them from the reducer's state would mean two
 * sources of truth about a thing that must be released exactly once - and in the
 * duel that was a real defect, where a connection that failed past the grace
 * window left the hunt hidden for good because the state it moved to did not
 * imply a hold.
 *
 * ## The lobby is POLLED, and that is not a shortcut
 *
 * Six chairs fill over minutes, in a table, with no socket anywhere near them -
 * the room does not exist until the roster is frozen. So the only honest way to
 * see somebody arrive is to ask the official read again, under the caller's own
 * cookie, scoped to the caller's own chair. The poll runs while the lobby screen
 * holds and stops the instant it does not; nothing about it is a subscription
 * this client invented.
 *
 * ## Nothing here chooses a team
 *
 * No request this object makes carries a team, a slot, a participant id or a
 * match. What a client says is a CODE, once, and the server answers with a chair.
 */

/* -------------------------------------------------------------------------- */
/*  Dependencies                                                              */
/* -------------------------------------------------------------------------- */

/** The socket, as this controller uses it. Never stored, never inspected. */
export interface PvpTeamControllerConnection {
  connect(): void;
  declareReady(): void;
  sendIntent(intent: PvpIntent): number | null;
  /**
   * Tells the room this chair chose to abandon (ADR 0043).
   *
   * Answers whether the frame was actually written. It is the one call here whose
   * answer matters: a player must never be told they left a match the room never
   * heard about.
   */
  requestLeave(): boolean;
  dispose(): void;
  subscribe(listener: (state: PvpTeamClientState) => void): () => void;
  /** As detonações de sangramento desde a chamada anterior (gate V28G.N). */
  drainBleedBursts(): readonly {
    readonly key: string;
    readonly p: string;
    readonly q: string;
    readonly n: number;
    /** O dano aplicado, em MILLI (gate V28G.S.1). */
    readonly d: bigint;
  }[];
  /** As reflexões confirmadas desde a chamada anterior (gate V28G.O). */
  drainReflects(): readonly {
    readonly key: string;
    readonly p: string;
    readonly q: string;
    readonly k: string;
    readonly s: 'i' | 'l';
    readonly x: number;
    readonly y: number;
    readonly tx: number;
    readonly ty: number;
    /** O dano aplicado, em MILLI, ou `null` numa interceção (V28G.S.1). */
    readonly d: bigint | null;
  }[];
  /** As explosoes confirmadas desde a chamada anterior (gate V28G.J). */
  drainBlasts(): readonly {
    readonly i: number;
    readonly e: string;
    readonly p: string;
    readonly t: number;
    readonly x: number;
    readonly y: number;
    readonly r: number;
    readonly q: readonly string[];
  }[];

  /** Ver o gemeo no duelo: a ligacao e quem deduplica. */
  drainProcs(): readonly {
    readonly i: number;
    readonly e: string;
    /* Quem foi curado, e o tique em que o facto nasceu. */
    readonly p: string;
    /* Em quem o efeito foi confirmado. Autoritativo (gate V28G.U). */
    readonly q: string;
    readonly t: number;
    /* A vida realmente acrescentada, em milli. Ausente se nao cura. */
    /** A cura, em MILLI e como string decimal (gate V28G.S.1). */
    readonly h?: string | undefined;
    readonly x: number;
    readonly y: number;
  }[];
}

export interface PvpTeamControllerDeps {
  /** `POST /api/pvp/team/lobby`. Opens a room and takes its first chair. */
  readonly createLobby: (signal: AbortSignal) => Promise<PvpTeamLobby>;
  /** `POST /api/pvp/team/join`. The code is the only thing sent. */
  readonly joinLobby: (code: string, signal: AbortSignal) => Promise<PvpTeamLobby>;
  /** `GET /api/pvp/team/lobby`. The caller's own chair, or nothing. */
  readonly readLobby: (signal: AbortSignal) => Promise<PvpTeamLobby | null>;
  /** `POST /api/pvp/team/launch`. Refused by the server for anybody but the owner. */
  readonly launchLobby: (signal: AbortSignal) => Promise<PvpTeamLobby>;
  /**
   * `POST /api/pvp/team/leave`. Gives the chair up.
   *
   * Called when a player closes a lobby they had not launched yet. A chair
   * silently kept would keep a room five people are waiting on, and the account
   * that holds it cannot open another one.
   */
  readonly leaveLobby: (signal: AbortSignal) => Promise<{ readonly cancelled: boolean }>;
  /** Builds a connection. One per generation; the old one is disposed first. */
  readonly openConnection: () => PvpTeamControllerConnection;
  /** How often the roster is re-read while the lobby screen holds. */
  readonly pollIntervalMs: number;
  /** Injected so a test can move the poll without waiting for it. */
  readonly schedule: (run: () => void, ms: number) => () => void;

  /* -- presentation -- */

  readonly acquirePause: () => () => void;
  readonly setHuntVisible: (visible: boolean) => void;
  readonly clearHuntIntent: () => void;
  /**
   * Opens the arena as a PARTICIPANT.
   *
   * The id is the room's own answer, out of the welcome - never the lobby's
   * chair read back, never an index into the roster, never a letter parsed out
   * of anything. `PvpScene` uses it to tell "mine" from "my side's" from
   * "theirs", and every one of those roles is the authority's.
   */
  readonly openArena: (participantId: string) => void;
  readonly publishAuthoritative: (state: PvpTeamClientState) => void;
  /** Ver `PvpControllerDeps.publishProcs`: factos, entregues uma vez. */
  readonly publishProcs: (
    cuts: readonly {
      readonly id: number;
      readonly procId: string;
      /* Quem foi curado, e quando: a chave visual e `(atacante, tique)`. */
      readonly attacker: string;
      /* Em QUEM o efeito foi confirmado. Autoritativo (gate V28G.U). */
      readonly target: string;
      readonly tick: number;
      /* A vida realmente acrescentada, em milli. Ausente se nao cura. */
      readonly healedMilli?: number | undefined;
      readonly x: number;
      readonly y: number;
    }[],
  ) => void;
  /**
   * As EXPLOSÕES confirmadas desde a publicação anterior (gate V28G.J).
   *
   * Separadas de `publishProcs` porque são outra coisa: um proc tem um alvo, e
   * uma explosão tem um centro, um raio e uma lista de quem levou. Uma bomba que
   * detona contra uma parede não atingiu ninguém, e não haveria alvo que pôr no
   * primeiro.
   */
  readonly publishBlasts: (
    blasts: readonly {
      readonly id: number;
      readonly effectId: string;
      readonly attacker: string;
      readonly tick: number;
      readonly x: number;
      readonly y: number;
      /* O raio MECÂNICO, em unidades de mundo. A cena desenha este. */
      readonly radius: number;
      /* Quem levou dano. Nunca quem estava por perto. */
      readonly hit: readonly string[];
    }[],
  ) => void;
  /**
   * As DETONAÇÕES de sangramento confirmadas desde a publicação anterior
   * (gate V28G.N).
   *
   * Separadas das duas de cima porque o facto tem outra forma: uma detonação não
   * tem projétil e não tem centro nem raio - ela acontece SOBRE um casco, por
   * ordem de um autor, com uma contagem de marcas. A chave já vem pronta e
   * deduplicada pela ligação.
   */
  readonly publishBleedBursts: (
    bursts: readonly {
      readonly key: string;
      readonly attacker: string;
      readonly target: string;
      readonly marks: number;
      /* O dano realmente aplicado. A cena imprime-o; nunca o recalcula. */
      /** O dano aplicado, em MILLI. A cena formata-o (gate V28G.S.1). */
      readonly damage: number;
    }[],
  ) => void;
  /**
   * As REFLEXÕES confirmadas, entregues UMA vez (gate V28G.O).
   *
   * Separadas das três de cima porque o facto tem outra forma: uma reflexão
   * acontece ENTRE dois cascos, e carrega as DUAS posições do tique em que
   * aconteceu. A chave já vem pronta e deduplicada pela ligação.
   */
  readonly publishReflects: (
    reflects: readonly {
      readonly key: string;
      readonly reflector: string;
      readonly target: string;
      readonly payload: string;
      readonly stage: 'i' | 'l';
      readonly x: number;
      readonly y: number;
      readonly tx: number;
      readonly ty: number;
      /* O dano realmente aplicado, ou `null` numa interceção sem dano ainda. */
      /** O dano aplicado, em MILLI, ou `null` numa interceção. */
      readonly damage: number | null;
    }[],
  ) => void;
  readonly closeArena: () => void;
  readonly intentSent: (seq: number, intent: PvpIntent) => void;

  /* -- the way back -- */

  readonly bootstrapHunt: (signal: AbortSignal) => Promise<void>;
}

/* -------------------------------------------------------------------------- */
/*  What the controller holds                                                 */
/* -------------------------------------------------------------------------- */

interface Owned {
  operation: number;
  abort: AbortController | null;
  connection: PvpTeamControllerConnection | null;
  unsubscribe: (() => void) | null;
  generation: number;
  pauseLease: (() => void) | null;
  arenaOpened: boolean;
  huntHidden: boolean;
  disposed: boolean;
  /** Set once the room has been seen complete, so entry runs exactly once. */
  entered: boolean;
  /** Cancels the roster poll. Non-null exactly while one is scheduled. */
  cancelPoll: (() => void) | null;
  /** Whether a chair is held that nobody has frozen yet. */
  holdsChair: boolean;
}

export interface PvpTeamController {
  subscribe(listener: (state: PvpTeamUiState) => void): () => void;
  getState(): PvpTeamUiState;
  open(): void;
  createLobby(): void;
  joinLobby(code: string): void;
  /** Freezes the roster. The server refuses anybody who does not own the lobby. */
  launch(): void;
  markReady(): void;
  codeEdited(): void;
  /** Asks the server whether this account is already in a team lobby. */
  resume(): void;
  sendIntent(intent: PvpIntent): void;
  leave(): void;
  retryHuntRecovery(): void;
  dispose(): void;
}

export function createPvpTeamController(deps: PvpTeamControllerDeps): PvpTeamController {
  let state: PvpTeamUiState = PVP_TEAM_INITIAL_STATE;
  const listeners = new Set<(state: PvpTeamUiState) => void>();
  const owned: Owned = {
    operation: 0,
    abort: null,
    connection: null,
    unsubscribe: null,
    generation: 0,
    pauseLease: null,
    arenaOpened: false,
    huntHidden: false,
    disposed: false,
    entered: false,
    cancelPoll: null,
    holdsChair: false,
  };

  function dispatch(event: PvpTeamUiEvent): void {
    const next = pvpTeamReducer(state, event);
    if (next === state) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener(state);
    }
  }

  /** A fresh id, which also invalidates every answer still in flight. */
  function nextOperation(): number {
    owned.abort?.abort();
    owned.operation += 1;
    owned.abort = new AbortController();
    return owned.operation;
  }

  const signal = (): AbortSignal => owned.abort?.signal as AbortSignal;

  /**
   * Whether an answer is still wanted.
   *
   * `AbortController` is help, not proof: a promise can resolve after an abort,
   * and a test double frequently does. The id is what decides.
   */
  const current = (operation: number): boolean => !owned.disposed && owned.operation === operation;

  /* ------------------------------------------------------------- polling -- */

  function stopPolling(): void {
    owned.cancelPoll?.();
    owned.cancelPoll = null;
  }

  /**
   * Re-reads the roster while the lobby screen holds, and stops otherwise.
   *
   * One timer, rescheduled after each answer rather than an interval: an
   * interval on a slow connection stacks requests, and six of those against a
   * route that reads six rows is a client attacking its own server.
   */
  function schedulePoll(): void {
    stopPolling();
    owned.cancelPoll = deps.schedule(() => {
      owned.cancelPoll = null;
      if (owned.disposed || state.kind !== 'lobby') {
        return;
      }
      deps
        .readLobby(signal())
        .then((lobby) => {
          if (owned.disposed || state.kind !== 'lobby') {
            return;
          }
          if (!lobby) {
            /*
             * The room is gone: cancelled by its owner, expired, or this account
             * left it somewhere else. Not an error the player did anything
             * about, and nothing was held.
             */
            owned.holdsChair = false;
            dispatch({ type: 'LOBBY_LOST' });
            return;
          }
          dispatch({ type: 'LOBBY_UPDATED', lobby });
          adoptLobby(lobby);
        })
        .catch(() => {
          /*
           * A poll that fails changes nothing.
           *
           * It is a read, not an authorisation. The next one is scheduled below
           * either way, so a blip costs one late roster rather than a screen.
           */
        })
        .finally(() => {
          if (!owned.disposed && state.kind === 'lobby') {
            schedulePoll();
          }
        });
    }, deps.pollIntervalMs);
  }

  /**
   * What to do with a roster that just arrived: connect, or keep waiting.
   *
   * The freeze is the ONLY signal, and it is the server's: `frozen` is derived
   * from the launch the server minted, so every one of the six clients learns it
   * from the same field at its own next read and opens its own socket. Nobody is
   * told to connect by anybody else, and no client decides the room is ready.
   */
  function adoptLobby(lobby: PvpTeamLobby): void {
    if (!lobby.frozen || state.kind !== 'lobby') {
      return;
    }
    stopPolling();
    connect(
      {
        matchId: lobby.matchId,
        participantId: lobby.you.participantId,
        team: lobby.you.team,
        slot: lobby.you.slot,
      },
      false,
    );
  }

  /* ---------------------------------------------------------- connection -- */

  function dropConnection(): void {
    owned.unsubscribe?.();
    owned.unsubscribe = null;
    owned.connection?.dispose();
    owned.connection = null;
  }

  function connect(match: PvpTeamMatchIdentity, resumed: boolean): void {
    dropConnection();
    const operation = nextOperation();
    dispatch({ type: 'CONNECT_STARTED', operation, match, resumed });
    owned.generation += 1;
    const generation = owned.generation;
    const connection = deps.openConnection();
    owned.connection = connection;
    owned.entered = false;

    owned.unsubscribe = connection.subscribe((client) => {
      /*
       * Everything a replaced socket says is dropped here. The room applies the
       * same rule to a superseded chair; applying it on this side too is what
       * stops an old generation repainting a live match, and it is checked
       * before the reducer so nothing reaches the arena either.
       */
      if (owned.disposed || generation !== owned.generation) {
        return;
      }
      onClientState(generation, client);
    });
    connection.connect();
  }

  function onClientState(generation: number, client: PvpTeamClientState): void {
    if (client.world.failure) {
      dispatch({
        type: 'CONNECTION_FAILED',
        generation,
        failure: client.world.failure,
        detail: client.world.failureDetail,
      });
      return;
    }

    /*
     * The room is complete, or was already fighting when this socket arrived.
     *
     * `running` belongs here as much as `ready` does: a client REJOINING after a
     * reload is welcomed into a match that is already under way and will never be
     * told `ready` again, so a condition that named only that state would leave
     * the panel reconnecting for ever with a live socket behind it.
     *
     * `entered` is what makes the transition run exactly once - a second frame
     * must not take the hunt down twice.
     */
    const live = client.world.matchState === 'ready' || client.world.matchState === 'running';
    const you = client.you;
    if (!owned.entered && live && you !== null) {
      owned.entered = true;
      owned.holdsChair = true;
      enterPvp(
        generation,
        {
          matchId: client.world.matchId ?? '',
          participantId: you.p,
          team: you.tm,
          slot: you.sl,
        },
        client,
      );
      return;
    }

    if (client.result) {
      dispatch({ type: 'ENDED', generation, result: client.result });
      publish(client);
      return;
    }
    dispatch({ type: 'AUTHORITATIVE_STATE', generation, state: client });
    publish(client);
  }

  /** Only ever from the generation that owns the arena. */
  function publish(client: PvpTeamClientState): void {
    if (owned.arenaOpened) {
      deps.publishAuthoritative(client);
      publishDrainedProcs();
      publishDrainedBlasts();
      publishDrainedBleedBursts();
      publishDrainedReflects();
    }
  }

  /** Ver o gemeo no controlador do duelo: a ligacao e quem deduplica. */
  function publishDrainedProcs(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const cuts = connection.drainProcs();
    if (cuts.length === 0) {
      return;
    }
    deps.publishProcs(
      cuts.map((cut) => ({
        id: cut.i,
        procId: cut.e,
        attacker: cut.p,
        target: cut.q,
        tick: cut.t,
        ...(cut.h === undefined ? {} : { healedMilli: Number(cut.h) }),
        x: cut.x,
        y: cut.y,
      })),
    );
  }

  /** A gémea de cima, para as DETONAÇÕES de sangramento (gate V28G.N). */
  function publishDrainedBleedBursts(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const bursts = connection.drainBleedBursts();
    if (bursts.length === 0) {
      return;
    }
    deps.publishBleedBursts(
      bursts.map((burst) => ({
        key: burst.key,
        attacker: burst.p,
        target: burst.q,
        marks: burst.n,
        /* Para a APRESENTAÇÃO, em milli. A vida que decide fica em `bigint`. */
        damage: Number(burst.d),
      })),
    );
  }

  /** A gémea de cima, para as REFLEXÕES (gate V28G.O). */
  function publishDrainedReflects(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const reflects = connection.drainReflects();
    if (reflects.length === 0) {
      return;
    }
    deps.publishReflects(
      reflects.map((reflect) => ({
        key: reflect.key,
        reflector: reflect.p,
        target: reflect.q,
        payload: reflect.k,
        stage: reflect.s,
        x: reflect.x,
        y: reflect.y,
        tx: reflect.tx,
        ty: reflect.ty,
        damage: reflect.d === null ? null : Number(reflect.d),
      })),
    );
  }

  /** A gémea de cima, para as explosões (gate V28G.J). */
  function publishDrainedBlasts(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const blasts = connection.drainBlasts();
    if (blasts.length === 0) {
      return;
    }
    deps.publishBlasts(
      blasts.map((blast) => ({
        id: blast.i,
        effectId: blast.e,
        attacker: blast.p,
        tick: blast.t,
        x: blast.x,
        y: blast.y,
        radius: blast.r,
        hit: [...blast.q],
      })),
    );
  }

  /* ------------------------------------------------------- transaction -- */

  /**
   * Takes the hunt down and puts the arena up, recording every step.
   *
   * Synchronous from the first step to the last on purpose: an `await` between
   * hiding the hunt and opening the arena is a frame in which the browser can
   * paint neither, and the player sees a blank game.
   */
  function enterPvp(
    generation: number,
    match: PvpTeamMatchIdentity,
    client: PvpTeamClientState,
  ): void {
    stopPolling();
    dispatch({ type: 'ENTER_STARTED', generation, match });
    const done = { lease: false, hidden: false, arena: false };
    try {
      deps.clearHuntIntent();
      owned.pauseLease = deps.acquirePause();
      done.lease = true;

      deps.setHuntVisible(false);
      owned.huntHidden = true;
      done.hidden = true;

      deps.openArena(match.participantId);
      owned.arenaOpened = true;
      done.arena = true;

      deps.publishAuthoritative(client);
      publishDrainedProcs();
      publishDrainedBlasts();
      publishDrainedBleedBursts();
      publishDrainedReflects();
      dispatch({ type: 'ENTERED', generation, state: client });
    } catch (error) {
      rollbackEntry(done);
      dispatch({
        type: 'ENTER_FAILED',
        generation,
        error: { reason: safeReason(error), recoverable: true },
      });
    }
  }

  /** Undoes exactly the steps that ran, and not one more. */
  function rollbackEntry(done: { lease: boolean; hidden: boolean; arena: boolean }): void {
    if (done.arena) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    if (done.hidden) {
      deps.setHuntVisible(true);
      owned.huntHidden = false;
    }
    if (done.lease) {
      releaseLease();
    }
  }

  function releaseLease(): void {
    owned.pauseLease?.();
    owned.pauseLease = null;
  }

  /* ------------------------------------------------------------- entry -- */

  function open(): void {
    dispatch({ type: 'OPEN' });
  }

  function codeEdited(): void {
    dispatch({ type: 'CODE_EDITED' });
  }

  function resume(): void {
    /*
     * Only from a closed panel, which is where a freshly loaded page is. A
     * player who has already opened the panel is telling us what they want, and
     * an answer that arrives afterwards must not take the screen away from them.
     */
    if (state.kind !== 'closed') {
      return;
    }
    const operation = nextOperation();
    deps
      .readLobby(signal())
      .then((lobby) => {
        if (!lobby || owned.disposed || state.kind !== 'closed' || !current(operation)) {
          return;
        }
        /*
         * The panel is NOT opened before the answer arrives. Opening first would
         * put this screen over the hunt on every page load, for every account,
         * and take it away again.
         */
        dispatch({ type: 'OPEN' });
        dispatch({ type: 'RESUME_LOBBY', operation, lobby });
        owned.holdsChair = true;
        if (lobby.frozen) {
          /*
           * Already launched: this is a reload into a match that may be running.
           * The chair is still this account's - the room holds it for its grace
           * window - and the capability is minted fresh against the database.
           */
          connect(
            {
              matchId: lobby.matchId,
              participantId: lobby.you.participantId,
              team: lobby.you.team,
              slot: lobby.you.slot,
            },
            true,
          );
          return;
        }
        schedulePoll();
      })
      .catch(() => {
        /*
         * A discovery that fails changes nothing. It is a hint, not an
         * authorisation: the player can still open the panel, and the server
         * refuses a second lobby by itself if there really was one.
         */
      });
  }

  function createLobby(): void {
    if (state.kind !== 'choosing') {
      return;
    }
    const operation = nextOperation();
    dispatch({ type: 'CREATE_REQUESTED', operation });
    deps
      .createLobby(signal())
      .then((lobby) => {
        if (!current(operation)) {
          return;
        }
        owned.holdsChair = true;
        dispatch({ type: 'CREATE_SUCCEEDED', operation, lobby });
        schedulePoll();
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        dispatch({
          type: 'CREATE_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function joinLobby(code: string): void {
    if (state.kind !== 'choosing') {
      return;
    }
    const normalised = code.trim().toUpperCase();
    const operation = nextOperation();
    dispatch({ type: 'JOIN_REQUESTED', operation });
    deps
      .joinLobby(normalised, signal())
      .then((lobby) => {
        if (!current(operation)) {
          return;
        }
        owned.holdsChair = true;
        dispatch({ type: 'JOIN_SUCCEEDED', operation, lobby });
        adoptLobby(lobby);
        if (state.kind === 'lobby') {
          schedulePoll();
        }
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        dispatch({
          type: 'JOIN_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  /**
   * Freezes the roster.
   *
   * The permission is the SERVER's - it refuses anybody who is not the account
   * that opened the lobby - and this method is the same request from anybody.
   * What the interface does with `teamOwnsLobby` is decide whether to draw a
   * button, which is presentation and not authorisation.
   */
  function launch(): void {
    if (state.kind !== 'lobby' || state.launching) {
      return;
    }
    dispatch({ type: 'LAUNCH_REQUESTED' });
    // Deliberately NOT a new operation: the roster poll is still wanted while
    // the freeze is in flight, and aborting it would leave the screen frozen on
    // whatever the last read said.
    deps
      .launchLobby(signal())
      .then((lobby) => {
        if (owned.disposed || state.kind !== 'lobby') {
          return;
        }
        dispatch({ type: 'LOBBY_UPDATED', lobby });
        adoptLobby(lobby);
      })
      .catch((error: unknown) => {
        if (owned.disposed || state.kind !== 'lobby') {
          return;
        }
        dispatch({
          type: 'LAUNCH_FAILED',
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function markReady(): void {
    if (state.kind !== 'ready' || state.declared) {
      // One per generation. A second click is not a second player, and the
      // server confirms readiness - sending it is not assuming it.
      return;
    }
    dispatch({ type: 'READY_REQUESTED', generation: state.generation });
    owned.connection?.declareReady();
  }

  /**
   * What the player is doing, on its way to the room.
   *
   * Guarded on `running` rather than on the connection alone: an intent sent
   * while the room is still `ready` belongs to a match that has not started, and
   * the connection drops it too.
   */
  function sendIntent(intent: PvpIntent): void {
    if (state.kind !== 'running') {
      return;
    }
    const seq = owned.connection?.sendIntent(intent);
    if (typeof seq === 'number') {
      deps.intentSent(seq, intent);
    }
  }

  /* -------------------------------------------------------------- exit -- */

  /**
   * The chair chose to abandon a fight that is under way (ADR 0043).
   *
   * The intent goes out and NOTHING else: the socket stays open so the room's
   * answer can arrive on it, the arena stays up, the lease stays held and the
   * hunt stays hidden. In a 3x3 that answer ends the match for all six clients in
   * favour of the other team - there is no 2x3 to continue into - and the walk
   * home runs afterwards, through the same result screen the other five see.
   *
   * Deliberately NOT `POST /api/pvp/team/leave`: that route dissolves a lobby and
   * refuses a running fight on purpose. Adding a second HTTP door into a live
   * room would be a door the room does not hold the lock for.
   */
  function requestAbandon(): void {
    if (state.kind !== 'running' || state.abandon === 'sending') {
      return;
    }
    const generation = state.generation;
    // Dispatched BEFORE the write, so a second click in the same frame finds the
    // state already busy rather than racing it.
    dispatch({ type: 'ABANDON_REQUESTED', generation });

    const connection = owned.connection;
    if (!connection || owned.generation !== generation || !connection.requestLeave()) {
      dispatch({ type: 'ABANDON_FAILED', generation });
    }
  }

  function leave(): void {
    if (state.kind === 'closed' || state.kind === 'leaving') {
      return;
    }
    /*
     * A fight is abandoned, never walked out of.
     *
     * Every other state keeps the exit it always had: the form and the lobby give
     * the chair up through the official route, a `reconnecting` socket has already
     * gone and the room holds the chair under its grace, and a `completed` match
     * is over - going back to the hunt IS leaving it.
     */
    if (state.kind === 'running') {
      requestAbandon();
      return;
    }
    /*
     * What this controller actually OWNS, not what the state implies.
     *
     * The duel learned this the hard way: a connection that fails past the grace
     * window moves the machine to `error`, and `error` does not hold the hunt -
     * so leaving from there released nothing and left the hunt hidden for good.
     */
    const wasHolding = owned.pauseLease !== null || owned.huntHidden;
    const wasOpen = owned.arenaOpened;
    const chairToGiveUp = owned.holdsChair && teamMatchOf(state) === null;

    stopPolling();
    nextOperation();
    dispatch({ type: 'LEAVE_REQUESTED' });
    dropConnection();

    if (chairToGiveUp) {
      /*
       * A chair given up through the official route, and only from a lobby that
       * never launched.
       *
       * After the freeze the chair belongs to a match: leaving it is a socket
       * that closed, which the room already understands as a walk-away and
       * handles with its own grace window and its own abandonment rule. Calling
       * the leave route there would ask the lobby to unmake a roster six
       * capabilities were minted against.
       *
       * The answer is not awaited and its failure changes nothing on screen: the
       * player has left either way, and the lobby expires on its own.
       */
      owned.holdsChair = false;
      void deps.leaveLobby(signal()).catch(() => undefined);
    }

    if (!wasHolding) {
      /*
       * The form, a lobby nobody launched, a socket that never reached a
       * complete room. Nothing was taken from the hunt, so there is no lease to
       * release, no visibility to restore and no bootstrap to ask for.
       */
      return;
    }
    if (wasOpen) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    // The hunt stays hidden and paused until the server has re-authorised it.
    startRecovery();
  }

  function startRecovery(): void {
    const operation = nextOperation();
    dispatch({ type: 'HUNT_RECOVERY_STARTED', operation });
    deps
      .bootstrapHunt(signal())
      .then(() => {
        if (!current(operation)) {
          return;
        }
        /*
         * Adopted FIRST, and only then shown. Restoring visibility before the
         * answer lands would put a hunt on screen that nobody has re-authorised.
         */
        deps.setHuntVisible(true);
        owned.huntHidden = false;
        releaseLease();
        dispatch({ type: 'HUNT_RECOVERED', operation });
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        dispatch({
          type: 'HUNT_RECOVERY_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function retryHuntRecovery(): void {
    if (state.kind !== 'recovering_hunt') {
      return;
    }
    // A NEW operation id, so the attempt that already failed cannot resolve this
    // one.
    startRecovery();
  }

  /* ----------------------------------------------------------- dispose -- */

  /**
   * Lets go of everything, once.
   *
   * React's cleanup cannot await a bootstrap, so this does NOT try to
   * re-authorise the hunt on the way out. What it must never do is leave the
   * hunt permanently invisible: the visibility is restored fail-safe here, and
   * the next mount runs its own bootstrap through the ordinary path.
   */
  function dispose(): void {
    if (owned.disposed) {
      return;
    }
    owned.disposed = true;
    stopPolling();
    owned.abort?.abort();
    owned.abort = null;
    dropConnection();

    if (owned.arenaOpened) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    if (owned.huntHidden) {
      deps.setHuntVisible(true);
      owned.huntHidden = false;
    }
    releaseLease();
    listeners.clear();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
    getState: () => state,
    open,
    createLobby,
    joinLobby,
    launch,
    codeEdited,
    resume,
    markReady,
    sendIntent,
    leave,
    retryHuntRecovery,
    dispose,
  };
}

/**
 * A reason a player can be shown.
 *
 * The API's machine reason when there is one, and a generic string otherwise.
 * Never a stack, never a ticket, never an id, never a code: an error message is
 * not a place to put something a screenshot would then carry.
 */
function safeReason(error: unknown): string {
  const reason = (error as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? reason : 'unavailable';
}

export type { PvpUiError };
